import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Search as SearchIcon, Loader2 } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { PeopleList } from "@/components/people-list";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchFollowers,
  fetchFollowing,
  fetchFollowingIds,
  type PersonSummary,
} from "@/lib/follows";
import { cn } from "@/lib/utils";

/**
 * Shared screen for /followers/$username and /following/$username so both lists
 * feel identical, with a live follow control on every row.
 */
export function RelationshipPage({
  username,
  kind,
}: {
  username: string;
  kind: "followers" | "following";
}) {
  const { user } = useAuth();
  const [people, setPeople] = useState<PersonSummary[]>([]);
  const [followingIds, setFollowingIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: prof } = await supabase
        .from("profiles")
        .select("id")
        .eq("username", username)
        .maybeSingle();
      const id = (prof as { id: string } | null)?.id;
      if (!id) {
        if (!cancelled) {
          setPeople([]);
          setLoading(false);
        }
        return;
      }
      const [list, mine] = await Promise.all([
        kind === "followers" ? fetchFollowers(id) : fetchFollowing(id),
        user ? fetchFollowingIds(user.id) : Promise.resolve(new Set<string>()),
      ]);
      if (cancelled) return;
      setPeople(list);
      setFollowingIds(mine);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [username, kind, user?.id]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return people;
    return people.filter(
      (p) => p.username.toLowerCase().includes(t) || (p.full_name ?? "").toLowerCase().includes(t),
    );
  }, [people, q]);

  return (
    <AppShell allowAnonymous>
      <div className="mx-auto max-w-2xl">
        <div className="flex items-center gap-3">
          <Link
            to="/profile/$username"
            params={{ username }}
            aria-label="Back to profile"
            className="grid h-11 w-11 place-items-center rounded-full text-foreground hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="min-w-0">
            <h1 className="truncate font-display text-xl font-bold tracking-tight">
              {kind === "followers" ? "Followers" : "Following"}
            </h1>
            <p className="truncate text-[13px] text-muted-foreground">@{username}</p>
          </div>
        </div>

        <div className="relative mt-5">
          <SearchIcon className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people"
            className="h-12 rounded-full pl-10"
          />
        </div>

        <div className={cn("mt-2 divide-y divide-border")}>
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <PeopleList
              people={filtered}
              followingIds={followingIds}
              empty={kind === "followers" ? "No followers yet." : "Not following anyone yet."}
              onFollowChange={(id, following) =>
                setFollowingIds((prev) => {
                  const next = new Set(prev);
                  if (following) next.add(id);
                  else next.delete(id);
                  return next;
                })
              }
            />
          )}
        </div>
      </div>
    </AppShell>
  );
}

export const Route = createFileRoute("/followers/$username")({
  head: ({ params }) => {
    const title = `Followers of @${params.username} — Xamseen`;
    const description = `People following @${params.username} on Xamseen.`;
    return {
      meta: [
        { title: title.slice(0, 60) },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "profile" },
        { name: "twitter:card", content: "summary" },
      ],
    };
  },
  component: FollowersRoute,
});

function FollowersRoute() {
  const { username } = Route.useParams();
  return <RelationshipPage username={username} kind="followers" />;
}
